import { FormProvider, useForm } from "react-hook-form";
import React,{ useState, useRef } from "react";
import {
    Box,
    Button,
    Collapse,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    useTheme,
} from "@mui/material";
import { Trans } from "@lingui/macro";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExpandLess } from "@mui/icons-material";
import StatsFilterForm from "../forms/StatsFilterForm";
import * as dayjs from "dayjs";

const StatisticsFilterPanel = ({
                                   newLabel,
                                   title,
                                   formModel,
                                   formList,
                                   filterDisabled,
                                   onFilterSubmit,
                                   onFilterReset,
                                   user,
                                   isMobile,
                                   setDataLoaded,
                                   setFilters,
                               }) => {
    const methods = useForm();
    const theme = useTheme();

    // stato per expand/collapse
    const [expanded, setExpanded] = useState(formList.map(() => false));

    // array di ref per ogni form
    const submitRefs = useRef(formList.map(() => React.createRef()));

    const init_filter_values = {};

    const handleExpandClick = (number) => {
        setExpanded((old) => old.map((o, i) => (i === number ? !o : o)));
    };

    const getSearchParams = (data) => {
        const subSection = data.SubSection;
        let searchParams = {};
        delete data.SubSection;
        Object.keys(data).forEach((section) => {
            Object.entries(data[section]).forEach(([key, value]) => {
                if (value !== undefined) {
                    if (key.includes("-To") || key.includes("-From")) {
                        const label = key.split("-");
                        searchParams = {
                            ...searchParams,
                            [`${section}.${label[0]}.${label[1].toLowerCase()}`]: dayjs(
                                value
                            ).toJSON(),
                        };
                    } else {
                        searchParams = { ...searchParams, [`${section}.${key}.equal`]: value };
                    }
                }
            });
        });
        return searchParams;
    };

    const handleFilterSubmit = (data) => {
        const searchParams = getSearchParams(data);

        const searchParamsArray = Object.entries(searchParams).map(([key, value]) => ({
            name: key,
            value,
        }));

        if (title !== "Custom charts") {
            if (setDataLoaded) setDataLoaded(false);
            if (setFilters) setFilters(searchParamsArray);
        } else {
            onFilterSubmit(searchParams);
        }
    };

    const closeAllSections = () => setExpanded(formList.map(() => false));

    const handleClickFilter = () => {
        // chiama requestSubmit di tutti i form montati
        submitRefs.current.forEach((ref) => {
            if (ref.current) ref.current.requestSubmit();
        });
        closeAllSections();
    };

    return (
        <Paper
            square
            sx={{
                display: "flex",
                flexDirection: "column",
                mt: 1,
                gap: 2,
                pt: 3,
                pb: 3,
                pl: 2,
                pr: 2,
                minHeight: 350,
            }}
        >
            <FormProvider {...methods} {...user}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                    <Typography
                        sx={{
                            color: theme.palette.mode === "light" ? theme.palette.newColors.gray : theme.palette.newColors.lightGray,
                            fontWeight: 600,
                        }}
                        variant="h5"
                    >
                        <Trans>{"Filters"}</Trans>
                    </Typography>

                    <Button
                        disabled={filterDisabled}
                        variant="contained"
                        sx={{ background: theme.palette.newColors.cyanGradient }}
                        size="small"
                        onClick={handleClickFilter}
                    >
                        <Trans>Filter</Trans>
                    </Button>

                    <Button
                        variant="contained"
                        size="small"
                        sx={{ background: theme.palette.newColors.cyanGradient }}
                        onClick={() => {
                            methods.reset(init_filter_values, { keepDefaultValues: false });
                            closeAllSections();
                            if (setDataLoaded && setFilters) {
                                setFilters([]);
                                setDataLoaded(false);
                            }
                            onFilterReset();
                        }}
                    >
                    <Trans>Reset</Trans>
                    </Button>
                </Box>

                <Divider flexitem sx={{ mt: -1 }} />

                <List
                    sx={{
                        width: "100%",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 1,
                    }}
                >
                    {formList.map((item, formNumber) => (
                        <Box key={item}>
                            <ListItemButton
                                onClick={() => handleExpandClick(formNumber)}
                                sx={{
                                    color: theme.mode === "light" && theme.palette.newColors.gray,
                                    borderBottom:
                                        formNumber !== formList.length - 1 && "1px solid #e0e0e0",
                                    px: 2,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    transition: "all 0.2s ease-in-out",
                                }}
                            >
                                <ListItemText
                                    primary={formModel[item].displayName}
                                    primaryTypographyProps={{
                                        fontWeight: expanded[formNumber] ? 600 : 500,
                                        fontSize: 15,
                                    }}
                                />
                                {expanded[formNumber] ? (
                                    <ExpandLess
                                        sx={{ color: theme.mode === "light" && theme.palette.newColors.gray }}
                                    />
                                ) : (
                                    <ExpandMoreIcon
                                        sx={{ color: theme.mode === "light" && theme.palette.newColors.gray }}
                                    />
                                )}
                            </ListItemButton>

                            <Collapse sx={{boxShadow:'-2px 0px 1px rgba(0, 0, 0, 0.2)'}} in={expanded[formNumber]} timeout="auto" unmountOnExit={false}>
                                <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: theme.mode === "light" && "#fafafa" }}>
                                    <StatsFilterForm
                                        title={title}
                                        isMobile={isMobile}
                                        formModel={formModel}
                                        submitRef={submitRefs.current[formNumber]}
                                        formList={formList}
                                        form={{
                                            number: formNumber,
                                            type: formList[formNumber],
                                            displayName: formModel[formList[formNumber]].displayName,
                                        }}
                                        handleFilterObjSubmit={handleFilterSubmit}
                                    />
                                </Box>
                            </Collapse>
                        </Box>
                    ))}
                </List>
            </FormProvider>
        </Paper>
    );
};

export default StatisticsFilterPanel;
